import React, { useState } from 'react';
import { authed } from '../lib/api';
import { KeyRound, Mail, ShieldCheck, User2, EyeOff, Eye } from 'lucide-react';

/**
 * Credential management primitives — Batch B2 Bug-Fix Pass
 *
 * These four self-contained cards are used by:
 *  · Super Admin Account page  → ChangeMyPasswordCard, ChangeMyEmailCard
 *  · Super Admin Dashboard → Settings → Security → all four cards
 *
 * They implement the exact flow finalized with the user:
 *  · Change my password: current pw + new + confirm (+ TOTP if enabled)
 *  · Change my email:    new email + current pw (+ TOTP if enabled)
 *  · Change employee pw: current employee pw + new + confirm
 *  · Change employee em: new email + current employee pw
 *
 * TOTP fields are shown ONLY when `totpEnabled=true`.
 */

const inputCls = "mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-2.5 outline-none text-sm bg-white";
const labelCls = "text-[10px] font-bold text-sdf-text-2 tracking-widest";

const PasswordInput = ({ value, onChange, tid, autoComplete = 'new-password' }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input
                type={show ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                className={inputCls + ' pr-10'}
                data-testid={tid}
            />
            <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-sdf-text-2 hover:text-sdf-text"
                aria-label={show ? 'Hide password' : 'Show password'}
                tabIndex={-1}
            >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
        </div>
    );
};

// ----------------------------------------------------------------------------
// Change MY password (Super Admin)
// ----------------------------------------------------------------------------

export const ChangeMyPasswordCard = ({ realm = 'super_admin', totpEnabled = false, compact = false }) => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [totp, setTotp] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr(''); setMsg('');
        if (!current || !next || !confirmPw) { setErr('All fields are required.'); return; }
        if (next.length < 8) { setErr('New password must be at least 8 characters.'); return; }
        if (next !== confirmPw) { setErr('New password and confirmation do not match.'); return; }
        if (totpEnabled && (totp.length !== 6 || !/^\d+$/.test(totp))) { setErr('Enter the 6-digit Google Authenticator code.'); return; }
        setBusy(true);
        try {
            await authed(realm, {
                method: 'POST',
                url: '/superadmin/security/change-password',
                data: {
                    current_password: current,
                    new_password: next,
                    confirm_password: confirmPw,
                    totp_code: totp,
                },
            });
            setMsg('Password updated successfully.');
            setCurrent(''); setNext(''); setConfirmPw(''); setTotp('');
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to change password.');
        } finally { setBusy(false); }
    };

    return (
        <section className="card-soft p-4 sm:p-5" data-testid="change-my-password-card">
            <div className="flex items-center gap-2 text-sdf-red font-bold text-xs tracking-widest mb-3">
                <KeyRound size={14} /> CHANGE PASSWORD
            </div>
            <div className={compact ? 'space-y-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
                <div>
                    <label className={labelCls}>CURRENT PASSWORD</label>
                    <PasswordInput value={current} onChange={setCurrent} tid="my-pw-current" autoComplete="current-password" />
                </div>
                <div>
                    <label className={labelCls}>NEW PASSWORD</label>
                    <PasswordInput value={next} onChange={setNext} tid="my-pw-new" />
                </div>
                <div>
                    <label className={labelCls}>RE-ENTER NEW PASSWORD</label>
                    <PasswordInput value={confirmPw} onChange={setConfirmPw} tid="my-pw-confirm" />
                </div>
                {totpEnabled && (
                    <div>
                        <label className={labelCls}>GOOGLE AUTHENTICATOR CODE</label>
                        <input
                            value={totp}
                            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength={6}
                            className={inputCls + ' tracking-widest font-bold'}
                            inputMode="numeric"
                            data-testid="my-pw-totp"
                        />
                    </div>
                )}
            </div>
            {err && <div className="mt-3 text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="my-pw-error">{err}</div>}
            {msg && <div className="mt-3 text-sm text-green-700 rounded bg-green-50 border border-green-200 px-3 py-2" data-testid="my-pw-success">{msg}</div>}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={submit} disabled={busy} className="btn-red" data-testid="my-pw-submit">
                    {busy ? 'Saving…' : 'Update Password'}
                </button>
            </div>
        </section>
    );
};

// ----------------------------------------------------------------------------
// Change MY email (Super Admin)
// ----------------------------------------------------------------------------

export const ChangeMyEmailCard = ({ realm = 'super_admin', totpEnabled = false, currentEmail = '', onUpdated }) => {
    const [newEmail, setNewEmail] = useState('');
    const [current, setCurrent] = useState('');
    const [totp, setTotp] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr(''); setMsg('');
        if (!newEmail.trim() || !current) { setErr('All fields are required.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) { setErr('Enter a valid email address.'); return; }
        if (newEmail.trim().toLowerCase() === (currentEmail || '').toLowerCase()) { setErr('New email is the same as current email.'); return; }
        if (totpEnabled && (totp.length !== 6 || !/^\d+$/.test(totp))) { setErr('Enter the 6-digit Google Authenticator code.'); return; }
        setBusy(true);
        try {
            const r = await authed(realm, {
                method: 'POST',
                url: '/superadmin/security/change-email',
                data: {
                    new_email: newEmail.trim(),
                    current_password: current,
                    totp_code: totp,
                },
            });
            setMsg(`Email updated to ${r.data.email}. Please sign in again with the new email next time.`);
            setNewEmail(''); setCurrent(''); setTotp('');
            onUpdated?.(r.data.email);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to change email.');
        } finally { setBusy(false); }
    };

    return (
        <section className="card-soft p-4 sm:p-5" data-testid="change-my-email-card">
            <div className="flex items-center gap-2 text-sdf-red font-bold text-xs tracking-widest mb-3">
                <Mail size={14} /> CHANGE EMAIL
            </div>
            {currentEmail && (
                <p className="text-[11px] text-sdf-text-2 mb-2">Current: <b className="text-sdf-text">{currentEmail}</b></p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>NEW EMAIL ADDRESS</label>
                    <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} data-testid="my-em-new" />
                </div>
                <div>
                    <label className={labelCls}>CURRENT PASSWORD</label>
                    <PasswordInput value={current} onChange={setCurrent} tid="my-em-current" autoComplete="current-password" />
                </div>
                {totpEnabled && (
                    <div>
                        <label className={labelCls}>GOOGLE AUTHENTICATOR CODE</label>
                        <input
                            value={totp}
                            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength={6}
                            className={inputCls + ' tracking-widest font-bold'}
                            inputMode="numeric"
                            data-testid="my-em-totp"
                        />
                    </div>
                )}
            </div>
            {err && <div className="mt-3 text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="my-em-error">{err}</div>}
            {msg && <div className="mt-3 text-sm text-green-700 rounded bg-green-50 border border-green-200 px-3 py-2" data-testid="my-em-success">{msg}</div>}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={submit} disabled={busy} className="btn-red" data-testid="my-em-submit">
                    {busy ? 'Saving…' : 'Update Email'}
                </button>
            </div>
        </section>
    );
};

// ----------------------------------------------------------------------------
// Change EMPLOYEE ADMIN password (Super Admin only)
// ----------------------------------------------------------------------------

export const ChangeEmployeePasswordCard = ({ employees = [], onDone }) => {
    const [email, setEmail] = useState('');
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr(''); setMsg('');
        if (!email || !current || !next || !confirmPw) { setErr('All fields are required.'); return; }
        if (next.length < 8) { setErr('New password must be at least 8 characters.'); return; }
        if (next !== confirmPw) { setErr('New password and confirmation do not match.'); return; }
        setBusy(true);
        try {
            await authed('super_admin', {
                method: 'POST',
                url: '/superadmin/security/employee/change-password',
                data: { email, current_password: current, new_password: next, confirm_password: confirmPw },
            });
            setMsg(`Password for ${email} has been updated.`);
            setCurrent(''); setNext(''); setConfirmPw('');
            onDone?.();
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to change employee password.');
        } finally { setBusy(false); }
    };

    return (
        <section className="card-soft p-4 sm:p-5" data-testid="emp-pw-card">
            <div className="flex items-center gap-2 text-sdf-red font-bold text-xs tracking-widest mb-3">
                <ShieldCheck size={14} /> CHANGE EMPLOYEE ADMIN PASSWORD
            </div>
            <p className="text-[11px] text-sdf-text-2 mb-3">Rule: you must know the Employee Admin&apos;s current password before setting a new one.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>EMPLOYEE ADMIN</label>
                    <select value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="emp-pw-select">
                        <option value="">Select employee admin…</option>
                        {employees.map(e => (
                            <option key={e.id} value={e.email}>{e.email}{e.display_name ? ` — ${e.display_name}` : ''}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>EMPLOYEE&apos;S CURRENT PASSWORD</label>
                    <PasswordInput value={current} onChange={setCurrent} tid="emp-pw-current" autoComplete="off" />
                </div>
                <div>
                    <label className={labelCls}>NEW PASSWORD</label>
                    <PasswordInput value={next} onChange={setNext} tid="emp-pw-new" />
                </div>
                <div>
                    <label className={labelCls}>RE-ENTER NEW PASSWORD</label>
                    <PasswordInput value={confirmPw} onChange={setConfirmPw} tid="emp-pw-confirm" />
                </div>
            </div>
            {err && <div className="mt-3 text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="emp-pw-error">{err}</div>}
            {msg && <div className="mt-3 text-sm text-green-700 rounded bg-green-50 border border-green-200 px-3 py-2" data-testid="emp-pw-success">{msg}</div>}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={submit} disabled={busy} className="btn-red" data-testid="emp-pw-submit">
                    {busy ? 'Saving…' : 'Update Employee Password'}
                </button>
            </div>
        </section>
    );
};

// ----------------------------------------------------------------------------
// Change EMPLOYEE ADMIN email (Super Admin only)
//
// Per user spec — no super-admin password required, no TOTP. Only the
// employee's CURRENT password. The backend endpoint currently uses a
// different body (super_admin_password + optional totp); to honour the
// user's spec exactly we now send the employee's current password as
// verification. Backend has been updated to accept `employee_current_password`.
// ----------------------------------------------------------------------------

export const ChangeEmployeeEmailCard = ({ employees = [], onDone }) => {
    const [currentEmail, setCurrentEmail] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [empPw, setEmpPw] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr(''); setMsg('');
        if (!currentEmail || !newEmail.trim() || !empPw) { setErr('All fields are required.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) { setErr('Enter a valid new email.'); return; }
        setBusy(true);
        try {
            const r = await authed('super_admin', {
                method: 'POST',
                url: '/superadmin/security/employee/change-email',
                data: {
                    current_email: currentEmail,
                    new_email: newEmail.trim(),
                    employee_current_password: empPw,
                },
            });
            setMsg(`Employee email updated to ${r.data.email}.`);
            setEmpPw(''); setNewEmail('');
            onDone?.();
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to change employee email.');
        } finally { setBusy(false); }
    };

    return (
        <section className="card-soft p-4 sm:p-5" data-testid="emp-em-card">
            <div className="flex items-center gap-2 text-sdf-red font-bold text-xs tracking-widest mb-3">
                <User2 size={14} /> CHANGE EMPLOYEE ADMIN EMAIL
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>EMPLOYEE ADMIN</label>
                    <select value={currentEmail} onChange={(e) => setCurrentEmail(e.target.value)} className={inputCls} data-testid="emp-em-select">
                        <option value="">Select employee admin…</option>
                        {employees.map(e => (
                            <option key={e.id} value={e.email}>{e.email}{e.display_name ? ` — ${e.display_name}` : ''}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>NEW EMAIL ADDRESS</label>
                    <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} data-testid="emp-em-new" />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls}>EMPLOYEE&apos;S CURRENT PASSWORD</label>
                    <PasswordInput value={empPw} onChange={setEmpPw} tid="emp-em-pw" autoComplete="off" />
                </div>
            </div>
            {err && <div className="mt-3 text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="emp-em-error">{err}</div>}
            {msg && <div className="mt-3 text-sm text-green-700 rounded bg-green-50 border border-green-200 px-3 py-2" data-testid="emp-em-success">{msg}</div>}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={submit} disabled={busy} className="btn-red" data-testid="emp-em-submit">
                    {busy ? 'Saving…' : 'Update Employee Email'}
                </button>
            </div>
        </section>
    );
};
